import type { ExportColumn, ExportFieldGroup } from './types';

export function columnLetter(index: number): string {
  let letter = '';
  for (let position = index + 1; position > 0; position = Math.floor((position - 1) / 26)) {
    letter = String.fromCharCode(65 + ((position - 1) % 26)) + letter;
  }
  return letter;
}

export function requiredColumns(groups: ExportFieldGroup[]): ExportColumn[] {
  return groups.flatMap((group) =>
    group.columns.filter((field) => field.required).map((field) => ({ field: field.id, title: field.title })),
  );
}

export function validateColumns(groups: ExportFieldGroup[], columns: ExportColumn[] | undefined): string | undefined {
  if (!Array.isArray(columns)) return 'Choose export columns.';
  const fields = new Map(groups.flatMap((group) => group.columns.map((field) => [field.id, field] as const)));
  const selected = new Set<string>();
  for (const column of columns) {
    if (!fields.has(column.field)) return 'An export field is no longer available.';
    if (selected.has(column.field)) return 'Each field can only be exported once.';
    selected.add(column.field);
    if (!column.title.trim()) return 'Enter a title for every column.';
    if (column.title.length > 32767) return 'Column titles cannot exceed 32767 characters.';
  }
  if ([...fields.values()].some((field) => field.required && !selected.has(field.id)))
    return 'Include all required fields.';
  return undefined;
}

export function reconcilePresetColumns(
  groups: ExportFieldGroup[],
  columns: ExportColumn[],
  legacy: ExportColumn[],
): { columns: ExportColumn[]; skipped: number } {
  const fields = new Map(groups.flatMap((group) => group.columns.map((field) => [field.id, field] as const)));
  const seen = new Set<string>();
  const available = columns.filter((column) => {
    if (!fields.has(column.field) || seen.has(column.field)) return false;
    seen.add(column.field);
    return true;
  });
  const legacyTitles = new Map(legacy.map((column) => [column.field, column.title]));
  const missingRequired = [...fields.values()]
    .filter((field) => field.required && !seen.has(field.id))
    .map((field) => ({ field: field.id, title: legacyTitles.get(field.id) ?? field.title }));
  return { columns: [...missingRequired, ...available], skipped: columns.length - available.length };
}

export function moveColumn(columns: ExportColumn[], from: number, to: number): ExportColumn[] {
  if (from === to || from < 0 || from >= columns.length || to < 0 || to >= columns.length) return columns;
  const next = [...columns];
  const [column] = next.splice(from, 1);
  next.splice(to, 0, column);
  return next;
}

export function dropColumn(
  columns: ExportColumn[],
  from: number,
  target: number,
  edge: 'top' | 'bottom',
): ExportColumn[] {
  if (from === target || target < 0 || target >= columns.length) return columns;
  const insertion = target + (edge === 'bottom' ? 1 : 0);
  return moveColumn(columns, from, insertion > from ? insertion - 1 : insertion);
}
